import { useParams, Link } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useQuery } from '@tanstack/react-query'
import { personnelService } from '../../../services'
import { Card, CardContent, CardHeader, CardTitle } from '../../../components/ui/card'
import { FileText, FolderOpen, ChevronRight } from 'lucide-react'

function DataRow({ label, value }: { label: string; value: string }) {
  return (
    <div className='flex items-center justify-between py-2.5 border-b border-neutral-50 last:border-b-0'>
      <span className='text-sm text-neutral-500'>{label}</span>
      <span className='text-sm text-neutral-900 font-medium'>{value}</span>
    </div>
  )
}

export default function EmployeePersonnelFileSection() {
  const { t } = useTranslation()
  const { id } = useParams()

  const { data: profile } = useQuery({
    queryKey: ['employee-profile', id],
    queryFn: () => personnelService.getEmployeeProfile(id!),
    enabled: !!id,
  })

  const { data: files } = useQuery({
    queryKey: ['employee-files', id],
    queryFn: () => personnelService.listEmployeeFiles(id!),
    enabled: !!id,
  })

  return (
    <div className='space-y-6'>
      <div className='grid grid-cols-1 md:grid-cols-2 gap-6'>
        <Card>
          <CardHeader>
            <div className='flex items-center gap-2'>
              <div className='w-8 h-8 rounded-lg bg-primary-50 flex items-center justify-center'>
                <FileText className='w-4 h-4 text-primary-500' />
              </div>
              <CardTitle>{t('common.personalData')}</CardTitle>
            </div>
          </CardHeader>
          <CardContent>
            <div>
              <DataRow label={t('common.salutation')} value={profile?.salutation || t('common.dash')} />
              <DataRow label={t('common.titleField')} value={profile?.title || t('common.dash')} />
              <DataRow
                label={t('employee.workHours')}
                value={
                  profile?.workHoursPerWeek != null
                    ? t('common.hoursPerWeek', { hours: profile.workHoursPerWeek })
                    : t('common.dash')
                }
              />
              <DataRow
                label={t('common.vacationDays')}
                value={
                  profile?.vacationDays != null
                    ? t('common.daysPerYear', { count: profile.vacationDays })
                    : t('common.dash')
                }
              />
              <DataRow label={t('common.shoeSize')} value={profile?.shoeSize || t('common.dash')} />
              <DataRow
                label={t('common.clothingSize')}
                value={profile?.clothingSize || t('common.dash')}
              />
            </div>
            <div className='mt-4'>
              <Link
                to='../personal-information'
                className='inline-flex items-center text-sm font-medium text-primary-600 hover:text-primary-700 transition-colors'
              >
                {t('common.viewPersonalData')}
                <ChevronRight className='w-3.5 h-3.5 ml-0.5' />
              </Link>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <div className='flex items-center gap-2'>
              <div className='w-8 h-8 rounded-lg bg-primary-50 flex items-center justify-center'>
                <FolderOpen className='w-4 h-4 text-primary-500' />
              </div>
              <CardTitle>{t('common.documents')}</CardTitle>
            </div>
          </CardHeader>
          <CardContent>
            <p className='text-sm text-neutral-500'>{t('common.documentsHint')}</p>
            <p className='text-sm font-medium text-neutral-900 mt-3'>
              {typeof files?.length === 'number'
                ? t('common.fileCount', { count: files.length })
                : t('common.dash')}
            </p>
            <div className='mt-4'>
              <Link
                to='../documents'
                className='inline-flex items-center text-sm font-medium text-primary-600 hover:text-primary-700 transition-colors'
              >
                {t('common.manageDocuments')}
                <ChevronRight className='w-3.5 h-3.5 ml-0.5' />
              </Link>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  )
}
